import type { GradeReference } from '../core/domain/Roaddata';
import type { MapViewSettings } from '../core/networkMap/MapViewSettingsPort';
import type MapViewSettingsPort from '../core/networkMap/MapViewSettingsPort';
import type { MapStyle } from '../types';

const MAP_VIEW_SETTINGS_LOCAL_STORAGE_KEY = 'ROADCARE_MAPVIEW_SETTINGS';

export type OrganizationMapViewSettings = {
    organizationId: string;
    settings: Partial<MapViewSettings>;
};

export type StoredOrganizationMapViewSettings = OrganizationMapViewSettings[];

export default class MapViewSettingsProvider implements MapViewSettingsPort {
    async getSettingsByOrganizationId(organizationId: string): Promise<Partial<MapViewSettings>> {
        return this.#getOrganizationSettings(organizationId)?.settings ?? {};
    }

    async updateMapStyleForOrganization(organizationId: string, mapStyle: MapStyle): Promise<void> {
        this.#updateOrganizatioSettings(organizationId, { mapStyle });
    }

    async updateDisplayPrForOrganization(organizationId: string, displayPr: boolean): Promise<void> {
        this.#updateOrganizatioSettings(organizationId, { displayPr });
    }

    async updateDisplayRoadsForOrganization(organizationId: string, displayRoads: boolean): Promise<void> {
        this.#updateOrganizatioSettings(organizationId, { displayRoads });
    }

    async updateDisplayDegradationsForOrganization(organizationId: string, displayDegradations: boolean): Promise<void> {
        this.#updateOrganizatioSettings(organizationId, { displayDegradations });
    }

    async updateSelectedGradeReferenceForOrganization(
        organizationId: string,
        selectedGradeReference: GradeReference,
    ): Promise<void> {
        this.#updateOrganizatioSettings(organizationId, { selectedGradeReference });
    }

    #updateOrganizatioSettings(organizationId: string, update: Partial<MapViewSettings>) {
        const settings = this.#getStoredSettings();

        const organizationSettings = settings.find((setting) => setting.organizationId === organizationId);

        if (organizationSettings) {
            Object.assign(organizationSettings.settings, update);
        } else {
            settings.push({
                organizationId,
                settings: update,
            });
        }

        localStorage.setItem(MAP_VIEW_SETTINGS_LOCAL_STORAGE_KEY, JSON.stringify(settings));
    }

    #getOrganizationSettings(organizationId: string): OrganizationMapViewSettings | null {
        const settings = this.#getStoredSettings();

        const organizationSettings = settings.find((setting) => setting.organizationId === organizationId);
        if (!organizationSettings) {
            return null;
        }

        return organizationSettings;
    }

    #getStoredSettings(): StoredOrganizationMapViewSettings {
        const settingsFromLocalStorage = localStorage.getItem(MAP_VIEW_SETTINGS_LOCAL_STORAGE_KEY);

        if (!settingsFromLocalStorage) {
            return [];
        }

        return JSON.parse(settingsFromLocalStorage);
    }
}
